<script setup lang="ts">
const props = defineProps<{
  keywords: readonly string[]
  horizontal?: boolean
}>()

defineEmits<{
  select: [keyword: string]
  clear: []
}>()

const keywordsRef = ref<HTMLDivElement | null>(null)
const canScrollLeft = ref(false)
const canScrollRight = ref(false)

// updateScrollEdges 只在对应方向存在隐藏内容时显示渐隐
const updateScrollEdges = () => {
  const element = keywordsRef.value
  canScrollLeft.value = Boolean(props.horizontal && element && element.scrollLeft > 1)
  canScrollRight.value = Boolean(props.horizontal && element && element.scrollLeft + element.clientWidth < element.scrollWidth - 1)
}

watch(() => props.keywords, updateScrollEdges, { flush: 'post' })

watch(
  [keywordsRef, () => props.horizontal],
  ([element, horizontal], _, onCleanup) => {
    updateScrollEdges()
    if (!element || !horizontal) {
      return
    }
    const observer = new ResizeObserver(updateScrollEdges)
    observer.observe(element)
    onCleanup(() => observer.disconnect())
  },
  { flush: 'post' }
)
</script>

<template>
  <section class="search-history" :class="{ 'search-history-horizontal': horizontal }" aria-label="搜索历史">
    <div class="history-head">
      <span>搜索历史</span>
      <button class="history-clear" type="button" title="清空搜索历史" aria-label="清空搜索历史" @click="$emit('clear')">
        <i class="fa-regular fa-trash-can" aria-hidden="true" />
      </button>
    </div>
    <div
      ref="keywordsRef"
      class="history-keywords"
      :class="{ 'can-scroll-left': canScrollLeft, 'can-scroll-right': canScrollRight }"
      @scroll.passive="updateScrollEdges"
    >
      <button
        v-for="keyword in keywords"
        :key="keyword"
        class="history-keyword"
        type="button"
        :title="keyword"
        @click="$emit('select', keyword)"
      >
        {{ keyword }}
      </button>
    </div>
  </section>
</template>

<style scoped>
.search-history {
  display: grid;
  gap: 8px;
  min-width: 0;
}

.history-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  color: var(--text-soft);
  font-size: 13px;
}

.history-clear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  border-radius: 999px;
  color: var(--text-soft);
  background: transparent;
  cursor: pointer;
}

.history-keywords {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  min-width: 0;
}

.history-keyword {
  min-width: 0;
  max-width: 100%;
  padding: 6px 10px;
  border: 0;
  border-radius: 999px;
  color: var(--text);
  background: var(--editor-bg);
  font: inherit;
  font-size: 13px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
  transition: color 0.16s ease, background-color 0.16s ease;
}

.history-keyword:hover,
.history-keyword:focus-visible {
  color: var(--brand);
  background: var(--brand-soft);
}

.search-history-horizontal .history-keywords {
  flex-wrap: nowrap;
  overflow-x: auto;
  overscroll-behavior-x: contain;
  scroll-padding-inline: 24px;
  scrollbar-width: none;
  mask-image: linear-gradient(
    to right,
    var(--history-fade-left, #000),
    #000 24px,
    #000 calc(100% - 24px),
    var(--history-fade-right, #000)
  );
}

.search-history-horizontal .history-keywords.can-scroll-left {
  --history-fade-left: transparent;
}

.search-history-horizontal .history-keywords.can-scroll-right {
  --history-fade-right: transparent;
}

.search-history-horizontal .history-keywords::-webkit-scrollbar {
  display: none;
}

.search-history-horizontal .history-keyword {
  flex-shrink: 0;
}

.history-clear:hover {
  color: var(--brand);
}
</style>
